/**
 * IQC Pay Popup Handler
 * This script handles the popup for the 'IQC Pay' manual payment method
 * on the Shopify checkout page.
 * 
 * @version 1.0.0
 */

// Debug mode flag - set to false in production
const DEBUG = false;

// Log helper
function logDebug(...args) {
  if (DEBUG) {
    console.log('[IQC Pay]', ...args);
  }
}

// Error logging
function logError(...args) {
  console.error('[IQC Pay Error]', ...args);
}

// One-time initialization on page load
document.addEventListener('DOMContentLoaded', function() {
  try {
    logDebug('Initializing IQC Pay handler');
    initIQCPayHandler();
  } catch (error) {
    logError('Initialization failed:', error);
  }
});

/**
 * Checks if the current page is a checkout page
 * @returns {boolean} True if on checkout page
 */
function isCheckoutPage() {
  // Check the URL path for checkout patterns
  const path = window.location.pathname;
  return path.includes('/checkout/') || 
         path.includes('/checkouts/') || 
         document.querySelector('form[data-payment-form]') !== null;
}

function initIQCPayHandler() {
  // Only initialize on checkout pages
  if (!isCheckoutPage()) {
    return;
  }
  
  // Watch for changes in the payment method selection
  const paymentMethodObserver = new MutationObserver(function(mutations) {
    checkForIQCPayMethod();
  });
  
  // Start observing checkout page changes
  const checkoutForm = document.querySelector('form[data-payment-form]');
  if (checkoutForm) {
    paymentMethodObserver.observe(checkoutForm, { 
      subtree: true, 
      childList: true,
      attributes: true 
    });
    
    // Also check immediately in case it's already selected
    checkForIQCPayMethod();
  }
}

function checkForIQCPayMethod() {
  // Look for the IQC Pay payment method radio button
  const paymentMethods = document.querySelectorAll('input[name="checkout[payment_gateway]"]');
  let iqcPaySelected = false;
  let iqcPayId = null;
  
  paymentMethods.forEach(method => {
    // Find the payment method by checking the label text
    const label = method.closest('div').querySelector('label');
    
    // Check for exact match of "IQC Pay" in the label
    const labelText = label ? label.textContent.trim() : '';
    const isIQCPay = labelText === 'IQC Pay' || 
                     labelText.includes('IQC Pay') || 
                     method.id.includes('iqc_pay') ||
                     method.value.includes('iqc_pay');
    
    if (isIQCPay) {
      iqcPayId = method.value;
      
      // Check if this method is selected
      if (method.checked) {
        iqcPaySelected = true;
      }
      
      // Add our click handler to this method
      if (!method.hasAttribute('data-iqcpay-handler')) {
        method.setAttribute('data-iqcpay-handler', 'true');
        method.addEventListener('change', function() {
          if (this.checked) {
            setupCompleteOrderButtonHandler();
          }
        });
      }
    }
  });
  
  // If IQC Pay is already selected, set up the button handler
  if (iqcPaySelected) {
    setupCompleteOrderButtonHandler();
  }
}

function setupCompleteOrderButtonHandler() {
  try {
    // Find the "Complete Order" button
    const completeOrderButton = document.querySelector('button[data-checkout-payment-button-target]');
    
    if (!completeOrderButton) {
      logDebug('Complete order button not found');
      return;
    }
    
    if (completeOrderButton.hasAttribute('data-iqcpay-click-handler')) {
      logDebug('Click handler already attached');
      return;
    }
    
    // Mark the button so we don't add multiple handlers
    completeOrderButton.setAttribute('data-iqcpay-click-handler', 'true');
    
    logDebug('Adding click handler to complete order button');
    
    // Override the button's click event
    completeOrderButton.addEventListener('click', function(event) {
      try {
        // Prevent the default form submission
        event.preventDefault();
        
        // Get the checkout ID from the URL
        const checkoutId = window.location.pathname.split('/').pop();
        
        if (!checkoutId) {
          logError('Could not extract checkout ID from URL');
          return false;
        }
        
        logDebug('Opening IQC Pay popup for checkout:', checkoutId);
        
        // Open the IQC Pay popup
        openIQCPayPopup(checkoutId);
        
        return false;
      } catch (error) {
        logError('Error in complete order button click handler:', error);
        return true; // Let the default action proceed
      }
    });
  } catch (error) {
    logError('Error setting up complete order button handler:', error);
  }
}

function openIQCPayPopup(checkoutId) {
  try {
    // Validate checkout ID
    if (!checkoutId) {
      logError('Invalid checkout ID');
      return;
    }
    
    logDebug('Creating IQC Pay popup for checkout:', checkoutId);
    
    // Create backdrop overlay
    const overlay = document.createElement('div');
    overlay.id = 'iqcpay-overlay';
  
    // Create popup container
    const popup = document.createElement('div');
    popup.id = 'iqcpay-popup';
  
    // Create popup header
    const header = document.createElement('div');
    header.className = 'iqcpay-popup-header';
  
    // Create popup title
    const title = document.createElement('h3');
    title.className = 'iqcpay-popup-title';
    title.textContent = 'IQC Pay Checkout';
    header.appendChild(title);
  
    // Create close button
    const closeButton = document.createElement('button');
    closeButton.className = 'iqcpay-popup-close';
    closeButton.innerHTML = '&times;';
    closeButton.setAttribute('aria-label', 'Close');
  
    // Close button event listener
    closeButton.addEventListener('click', async function() {
      // Check the transaction status before closing
      const statusCheck = await checkFlexiPayTransactionStatus(checkoutId);
      
      // Remove the overlay
      document.body.removeChild(overlay);
      
      // Only submit the order if payment is complete
      if (statusCheck) {
        submitOrderForm();
      } else {
        // Show a message that payment was not completed
        const messageContainer = document.createElement('div');
        messageContainer.style.backgroundColor = '#fff3cd';
        messageContainer.style.color = '#856404';
        messageContainer.style.padding = '12px';
        messageContainer.style.margin = '10px 0';
        messageContainer.style.borderRadius = '4px';
        messageContainer.style.border = '1px solid #ffeeba';
        messageContainer.style.fontSize = '14px';
        messageContainer.textContent = 'Payment was not completed. Please try again or choose a different payment method.';
        
        // Find the payment section and insert the message
        const paymentSection = document.querySelector('form[data-payment-form]');
        if (paymentSection) {
          paymentSection.insertAdjacentElement('afterbegin', messageContainer);
        }
      }
    });
  
    header.appendChild(closeButton);
    popup.appendChild(header);
  
    // Create popup content
    const content = document.createElement('div');
    content.className = 'iqcpay-popup-content';
  
    // Create loading indicator
    const loading = document.createElement('div');
    loading.className = 'iqcpay-loading';
    const spinner = document.createElement('div');
    spinner.className = 'iqcpay-spinner';
    loading.appendChild(spinner);
    content.appendChild(loading);
  
    // Create iframe for the FlexiPay content
    const iframe = document.createElement('iframe');
    iframe.className = 'iqcpay-iframe';
    iframe.src = `https://sonlightflexypaysandbox.kensiumcommerce.com/checkout/${checkoutId}`;
    iframe.setAttribute('title', 'IQC Pay Checkout');
  
    // Remove loading indicator once iframe is loaded
    iframe.addEventListener('load', function() {
      if (loading.parentNode) {
        content.removeChild(loading);
      }
    });
  
    // Add message listener for communication from iframe
    window.addEventListener('message', async function(event) {
      // Verify origin for security
      if (event.origin === 'https://sonlightflexypaysandbox.kensiumcommerce.com') {
        // Check if data is a string or an object with status property
        const isComplete = event.data === 'payment_complete' || 
                          (typeof event.data === 'object' && 
                           event.data !== null && 
                           event.data.status === 'payment_complete');
                             
        if (isComplete) {
          // Double-check transaction status with our API
          const statusCheck = await checkFlexiPayTransactionStatus(checkoutId);
          
          // Remove the overlay
          document.body.removeChild(overlay);
          
          // Only proceed if payment is confirmed
          if (statusCheck) {
            submitOrderForm();
          } else {
            // Show a message that payment verification failed
            const messageContainer = document.createElement('div');
            messageContainer.style.backgroundColor = '#fff3cd';
            messageContainer.style.color = '#856404';
            messageContainer.style.padding = '12px';
            messageContainer.style.margin = '10px 0';
            messageContainer.style.borderRadius = '4px';
            messageContainer.style.border = '1px solid #ffeeba';
            messageContainer.style.fontSize = '14px';
            messageContainer.textContent = 'Payment verification failed. Please try again or contact customer support.';
            
            // Find the payment section and insert the message
            const paymentSection = document.querySelector('form[data-payment-form]');
            if (paymentSection) {
              paymentSection.insertAdjacentElement('afterbegin', messageContainer);
            }
          }
        }
      }
    });
  
    content.appendChild(iframe);
    popup.appendChild(content);
    overlay.appendChild(popup);
  
    // Add to page
    document.body.appendChild(overlay);
  } catch (error) {
    logError('Error opening IQC Pay popup:', error);
  }
}

/**
 * Checks the status of a FlexiPay transaction
 * @param {string} checkoutId - The Shopify checkout ID
 * @returns {Promise<boolean>} - Promise resolving to true if payment is complete
 */
async function checkFlexiPayTransactionStatus(checkoutId) {
  try {
    // Construct the status URL
    const statusUrl = `https://sonlightflexypaysandbox.kensiumcommerce.com/api/transaction-status?checkout_id=${checkoutId}`;
    
    // Fetch the transaction status
    const response = await fetch(statusUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });
    
    // Parse the response
    const data = await response.json();
    
    // Check if the transaction was successful
    if (data.success && data.status === 'completed') {
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking FlexiPay transaction status:', error);
    return false;
  }
}

function submitOrderForm() {
  // Find the "Complete Order" button and simulate a native click
  const completeOrderButton = document.querySelector('button[data-checkout-payment-button-target]');
  if (completeOrderButton) {
    // Remove our click handler temporarily
    const oldAttribute = completeOrderButton.getAttribute('data-iqcpay-click-handler');
    completeOrderButton.removeAttribute('data-iqcpay-click-handler');
    
    // Create and dispatch a native click event
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    completeOrderButton.dispatchEvent(clickEvent);
    
    // Restore our handler attribute
    setTimeout(function() {
      completeOrderButton.setAttribute('data-iqcpay-click-handler', oldAttribute);
    }, 100);
  }
} 